/**
 * Global Keyboard Shortcuts System
 *
 * Provides keyboard shortcuts for common actions throughout the application.
 * Shortcuts are context-aware and can be enabled/disabled per view.
 *
 * Usage:
 * ```ts
 * import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
 *
 * const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
 *
 * // Register a shortcut
 * registerShortcut('ctrl+s', () => saveDocument(), { description: 'Save document' });
 *
 * // Unregister when component unmounts
 * onUnmounted(() => unregisterShortcut('ctrl+s'));
 * ```
 */

import { ref, readonly } from 'vue';

export interface Shortcut {
  key: string;
  handler: () => void;
  description: string;
  category?: string;
  enabled?: boolean;
}

interface ShortcutRegistry {
  [key: string]: Shortcut;
}

// Global registry of shortcuts
const shortcuts = ref<ShortcutRegistry>({});

// Track if shortcuts are globally enabled (disabled in inputs)
const shortcutsEnabled = ref(true);

// Track if help modal is shown
const showHelp = ref(false);

// For sequential shortcuts (e.g., "g+h" = press g, then h)
let sequenceBuffer: string[] = [];
let sequenceTimeout: ReturnType<typeof setTimeout> | null = null;
const SEQUENCE_TIMEOUT = 500; // ms to wait for next key in sequence

/**
 * Normalize a key combination string
 * Converts to lowercase and sorts modifiers
 */
function normalizeKey(key: string): string {
  const parts = key.toLowerCase().split('+').map(p => p.trim());
  const modifiers: string[] = [];
  // Preserve ALL non-modifier keys in order. A multi-key combo with no
  // modifiers (e.g. "g+a") is a SEQUENCE (press g, then a) — collapsing it to
  // the last key would turn it into a single-letter shortcut that fires while
  // typing (e.g. "a" navigating to Accounting).
  const mainKeys: string[] = [];

  for (const part of parts) {
    if (['ctrl', 'alt', 'shift', 'meta', 'cmd'].includes(part)) {
      // Normalize 'cmd' to 'meta' for cross-platform
      modifiers.push(part === 'cmd' ? 'meta' : part);
    } else {
      mainKeys.push(part);
    }
  }

  // Sort modifiers for consistent comparison; keep main keys in sequence order.
  modifiers.sort();
  return [...modifiers, ...mainKeys].join('+');
}

/**
 * Convert a keyboard event to a normalized key string
 */
function eventToKey(event: KeyboardEvent): string | null {
  // Guard against undefined event.key (can happen with some keyboard events)
  if (!event.key) return null;

  const parts: string[] = [];

  if (event.ctrlKey) parts.push('ctrl');
  if (event.altKey) parts.push('alt');
  if (event.shiftKey) parts.push('shift');
  if (event.metaKey) parts.push('meta');

  // Get the key, handling special cases
  let key = event.key.toLowerCase();

  // Handle special keys
  if (key === ' ') key = 'space';
  if (key === 'escape') key = 'esc';
  if (key === 'arrowup') key = 'up';
  if (key === 'arrowdown') key = 'down';
  if (key === 'arrowleft') key = 'left';
  if (key === 'arrowright') key = 'right';

  parts.push(key);
  parts.sort((a, b) => {
    const order = ['ctrl', 'alt', 'shift', 'meta'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });

  return parts.join('+');
}

/**
 * Check if the event target is an input element
 */
function isInputElement(target: EventTarget | null): boolean {
  if (!target) return false;
  const element = target as HTMLElement;
  const tagName = element.tagName?.toLowerCase();

  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    element.isContentEditable
  );
}

/**
 * Clear the sequence buffer
 */
function clearSequence(): void {
  sequenceBuffer = [];
  if (sequenceTimeout) {
    clearTimeout(sequenceTimeout);
    sequenceTimeout = null;
  }
}

/**
 * Check if a key is a modifier
 */
function isModifierKey(key: string): boolean {
  return ['ctrl', 'alt', 'shift', 'meta', 'control'].includes(key.toLowerCase());
}

/**
 * Global keyboard event handler
 */
function handleKeyDown(event: KeyboardEvent): void {
  // Skip if shortcuts are disabled
  if (!shortcutsEnabled.value) return;

  // Skip if typing in an input (unless it's a global shortcut like Escape or F1)
  const isInput = isInputElement(event.target);
  const key = eventToKey(event);

  // Skip if we couldn't parse the key
  if (!key) return;

  // Always allow Escape and F1 to work (F1 is help key)
  const globalKeys = ['esc', 'f1'];
  if (!globalKeys.includes(key) && isInput) return;

  // Prevent default for F1 (browser help) before anything else
  if (key === 'f1') {
    event.preventDefault();
  }

  // Check for help shortcut (? or shift+/)
  if (key === 'shift+?' || key === '?') {
    event.preventDefault();
    showHelp.value = !showHelp.value;
    clearSequence();
    return;
  }

  // Skip pure modifier key presses
  if (isModifierKey(event.key)) return;

  // First, try to match as a regular shortcut (with modifiers)
  const shortcut = shortcuts.value[key];
  if (shortcut && shortcut.enabled !== false) {
    event.preventDefault();
    shortcut.handler();
    clearSequence();
    return;
  }

  // For sequential shortcuts (no modifiers), add to buffer
  if (!event.ctrlKey && !event.altKey && !event.metaKey) {
    // Clear previous timeout and set new one
    if (sequenceTimeout) {
      clearTimeout(sequenceTimeout);
    }

    // Get the simple key (no modifiers)
    const simpleKey = event.key.toLowerCase();
    sequenceBuffer.push(simpleKey);

    // Try to match the sequence
    const sequenceKey = sequenceBuffer.join('+');
    const sequenceShortcut = shortcuts.value[sequenceKey];

    if (sequenceShortcut && sequenceShortcut.enabled !== false) {
      event.preventDefault();
      sequenceShortcut.handler();
      clearSequence();
      return;
    }

    // Set timeout to clear buffer
    sequenceTimeout = setTimeout(clearSequence, SEQUENCE_TIMEOUT);
  }
}

// Set up global listener
let listenerAttached = false;

function attachListener(): void {
  if (listenerAttached) return;
  window.addEventListener('keydown', handleKeyDown);
  listenerAttached = true;
}

/**
 * Register a keyboard shortcut
 */
function registerShortcut(
  key: string,
  handler: () => void,
  options: { description: string; category?: string; enabled?: boolean } = { description: '' }
): void {
  const normalizedKey = normalizeKey(key);
  shortcuts.value[normalizedKey] = {
    key: normalizedKey,
    handler,
    description: options.description,
    category: options.category || 'General',
    enabled: options.enabled ?? true,
  };

  // Ensure listener is attached
  attachListener();
}

/**
 * Unregister a keyboard shortcut
 */
function unregisterShortcut(key: string): void {
  const normalizedKey = normalizeKey(key);
  delete shortcuts.value[normalizedKey];
}

/**
 * Enable or disable a shortcut
 */
function setShortcutEnabled(key: string, enabled: boolean): void {
  const normalizedKey = normalizeKey(key);
  if (shortcuts.value[normalizedKey]) {
    shortcuts.value[normalizedKey].enabled = enabled;
  }
}

/**
 * Disable all shortcuts temporarily
 */
function disableAllShortcuts(): void {
  shortcutsEnabled.value = false;
}

/**
 * Enable all shortcuts
 */
function enableAllShortcuts(): void {
  shortcutsEnabled.value = true;
}

/**
 * Get all registered shortcuts grouped by category
 */
function getShortcutsByCategory(): Record<string, Shortcut[]> {
  const grouped: Record<string, Shortcut[]> = {};

  for (const shortcut of Object.values(shortcuts.value)) {
    const category = shortcut.category || 'General';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(shortcut);
  }

  return grouped;
}

/**
 * Format a key combination for display
 */
export function formatKeyForDisplay(key: string): string {
  return key
    .split('+')
    .map(part => {
      switch (part) {
        case 'ctrl':
          return 'Ctrl';
        case 'alt':
          return 'Alt';
        case 'shift':
          return 'Shift';
        case 'meta':
          return navigator.platform.includes('Mac') ? '⌘' : 'Win';
        case 'esc':
          return 'Esc';
        case 'space':
          return 'Space';
        case 'up':
          return '↑';
        case 'down':
          return '↓';
        case 'left':
          return '←';
        case 'right':
          return '→';
        default:
          return part.toUpperCase();
      }
    })
    .join(' + ');
}

/**
 * Keyboard shortcuts composable
 */
export function useKeyboardShortcuts() {
  // Attach listener on first use
  attachListener();

  return {
    /**
     * All registered shortcuts (readonly)
     */
    shortcuts: readonly(shortcuts),

    /**
     * Whether shortcuts help is shown
     */
    showHelp,

    /**
     * Whether shortcuts are globally enabled
     */
    shortcutsEnabled: readonly(shortcutsEnabled),

    /**
     * Register a keyboard shortcut
     */
    registerShortcut,

    /**
     * Unregister a keyboard shortcut
     */
    unregisterShortcut,

    /**
     * Enable or disable a specific shortcut
     */
    setShortcutEnabled,

    /**
     * Disable all shortcuts temporarily
     */
    disableAllShortcuts,

    /**
     * Enable all shortcuts
     */
    enableAllShortcuts,

    /**
     * Get shortcuts grouped by category
     */
    getShortcutsByCategory,

    /**
     * Format a key for display
     */
    formatKeyForDisplay,

    /**
     * Toggle help modal
     */
    toggleHelp: () => {
      showHelp.value = !showHelp.value;
    },
  };
}

// Export singleton for direct import
export const keyboardShortcuts = useKeyboardShortcuts();
